import data from "../../shared/sharedData";
export default {
  name: "Employer Point-of-Contact Information",
  symbol: "list",
  pageInfo: {
    text: `An employer point of contact is an employee of the employer whose position authorizes the employee to provide information and supporting documentation concerning this Form ETA-9141C, <em>Application for a Prevailing Wage Determination</em>, and to communicate with the Department of Labor on behalf of the employer.  The employer point of contact should be the individual most familiar with the content of this application and circumstances of the foreign worker’s employment.<br /><br /><strong><u>Note</u></strong>:  The employer point of contact information in this Section, specifically the name, telephone number, and email address, must be different from the attorney/agent information listed in Section D, except when an attorney listed in Section D is an employee of the employer. `,
    coloring: "grey",
  },
  sections: [
    {
      name: "Name & Title",
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer Point of Contact profile to populate this section (optional)",
          code: "B",
          prefill: {
            schema: "employerPocs",
            type: "employer-pocs",
            formName: "9141C",
            formSection: "B",
          },
        },
        {
          code: "B.1",
          machine_name: "requestorPocLastName",
          widget: "text",
          label: "Contact’s Last (family) Name",
          maxLength: 35,
          validate: [
            "required",
            {
              minLength: 2,
              message: "Last Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip:
            "Enter the last (family) name of the employer's point of contact.",
        },
        {
          code: "B.2",
          machine_name: "requestorPocFirstName",
          widget: "text",
          label: "First (given) Name",
          maxLength: 35,
          validate: [
            "required",
            {
              minLength: 2,
              message: "First Name must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip:
            "Enter the first (given) name of the employer's point of contact.",
        },
        {
          code: "B.3",
          machine_name: "requestorPocMiddleName",
          widget: "text",
          label: "Middle Name(s) (if applicable) §",
          maxLength: 35,
          tooltip:
            "Enter the middle name(s) of the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
        },
        {
          code: "B.4",
          machine_name: "requestorPocJobTitle",
          widget: "text",
          label: "Contact's Job Title",
          maxLength: 60,
          validate: ["required", "noNAs"],
          tooltip: "Enter the job title of the employer's point of contact.",
        },
      ],
    },
    {
      name: "Address",
      fields: [
        {
          code: "B.5",
          machine_name: "requestorPocAddr1",
          widget: "text",
          label: "Address 1",
          maxLength: 60,
          validate: [
            "required",
            {
              minLength: 2,
              message: `Address 1 must contain at least 2 characters.`,
            },
            "noNAs",
          ],
          tooltip:
            "Enter the business street address for the employer’s point of contact.  The address must be a physical location.  Since the address conventions in the CNMI do not always follow the formats generally for the continental United States, see the Address Note for CNMI at the end of these general instructions.",
        },
        {
          code: "B.6",
          machine_name: "requestorPocAddr2",
          widget: "text",
          label: "Address 2",
          maxLength: 60,
          tooltip: `This field should contain the CNMI Island, as applicable.  If additional space is needed for the address or entry of a Post Office (P.O.) Box, use this field to complete the address.   If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "B.10",
          machine_name: "requestorPocCountry",
          widget: "select",
          label: "Country",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["requestorPocState"],
          tooltip: "Enter the Country of the employer's point of contact.",
        },
        {
          code: "B.7",
          machine_name: "requestorPocCity",
          widget: "text",
          label: "City",
          maxLength: 30,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip: "Enter the city of the employer's point of contact.",
        },
        {
          code: "B.8",
          machine_name: "requestorPocState",
          widget: "select",
          label: "State",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          isVisible: {
            when: "requestorPocCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip:
            "Enter the State, District, or Territory of the employer’s point of contact.",
        },
        {
          code: "B.8",
          machine_name: "requestorPocState",
          widget: "text",
          label: "State",
          maxLength: 30,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          isVisible: {
            when: "requestorPocCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "!==",
          },
          validate: ["required"],
          tooltip:
            "Enter the State, District, or Territory of the employer’s point of contact.",
        },
        {
          code: "B.9",
          machine_name: "requestorPocPostalCode",
          widget: "text",
          label: "Postal Code",
          maxLength: 10,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            "required",
            {
              postalCountry: "requestorPocCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "requestorPocCountry",
              },
            },
            {
              postalCountry: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip:
            "Enter the postal (zip) code of the employer’s point of contact.",
        },
        {
          code: "B.11",
          machine_name: "requestorPocProvince",
          widget: "text",
          label: "Province (if applicable) §",
          maxLength: 25,
          disabled: true,
          disableWhenField: "requestorPocCountry",
          validate: [
            {
              isRequired: "requestorPocCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          // isVisible: {
          //   when: "requestorPocCountry",
          //   is: ["UNITED STATES OF AMERICA", "- Select -", ""],
          //   operator: "!==",
          //   hideStyles: true,
          // },
          tooltip:
            "Enter the province of the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Contact Information",
      fields: [
        {
          code: "B.12",
          machine_name: "requestorPocPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer’s point of contact.  Include country code, if the point of contact is located outside of the United States.",
        },
        {
          code: "B.13",
          machine_name: "requestorPocPhoneExt",
          widget: "text",
          label: "Extension (if applicable) §",
          maxLength: 4,
          validate: ["number"],
          parse: "positiveWholeNumber",
          tooltip:
            "Enter the extension of the telephone number of the employer’s point of contact, if applicable.  Enter “N/A” if not applicable.",
        },
        {
          code: "B.14",
          machine_name: "requestorPocEmail",
          widget: "text",
          label: "Business E-Mail Address",
          maxLength: 100,
          validate: [
            "required",
            {
              email: {
                extraData: {
                  onlyOne: true,
                },
              },
            },
          ],
          tooltip:
            "Enter the business e-mail address of the employer's point of contact in the format <u>name@emailaddress.top-level domain</u>. The email entered in this field must be the same as the one regularly used by the requestor’s point of contact for its business operations and capable of sending and receiving electronic communications from the Department with respect to the processing of this application. If the requestor’s point of contact does not possess a business email address, please enter “N/A”.",
        },
      ],
    },
  ],
};
